import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useSelector } from "react-redux";

import type { PaymentModalTitle } from "@web-solutions/core/interfaces/payment-modal";

import Analytics from "@web-solutions/module-analytics";

import { EVENT_ACTION } from "core/constants/general";

import { useRemoteConfig } from "core/hooks/use-remote-config";
import { SolidMetadata } from "core/interfaces/billing";
import { OneClickPayment, OneClickPaymentRef } from "core/one-time/one-click";

import {
  PaymentProcessor,
  PaymentProcessorRef,
} from "core/payment/payment-processor-provider/components/payment-processor";
import ErrorPopup from "core/payment/base/components/error-popup";
import { ProductDetails, selectPaymentSystem } from "core/store/billing/selectors";
import { PaymentSystem } from "@web-solutions/react-billing";

import {
  OneTimePaymentArgs,
  OneTimePaymentRef,
} from "core/one-time/constans/types";

import { useError } from "core/one-time/hooks/use-error";
import { useOneClickPayment } from "core/one-time/hooks/use-one-click-payment";
import { useLoadOrders } from "core/one-time/hooks/use-load-orders";
import { usePaymentMethods } from "core/one-time/hooks/use-payment-methods";
import { usePaymentHandlers } from "core/one-time/hooks/use-payment-handlers";

import { ERROR_CODES, generalCategory } from "./constans";
import { Preloader } from "core/ui-elements";
export interface OneTimePaymentProps {
  category: string;
  trigger: string;
  metadata?: any;
  solidMetadata?: SolidMetadata;
  activeProduct?: ProductDetails;
  oneTimeProducts: ProductDetails[] | null;
  analyticsInfo?: { [key: string]: any };
  paymentModalTitle?: PaymentModalTitle;
  withTransactionDetailsEmail?: boolean;
  isOnlyCard?: boolean;
  isOnlyPayPal?: boolean;
  errorPopupWithPayPal?: boolean;
  onSuccess: (data: any) => void;
  onSkipClick?: () => void;
  onClose?: () => void;
  onOneClickFail?: (error?: string, code?: string | number) => void;
  onError?: (error: any) => void;
  onSecondRequestSubmit?: () => void;
}

export const OneTimePayment = React.forwardRef<
  OneTimePaymentRef,
  OneTimePaymentProps
>(
  (
    {
      category,
      activeProduct,
      oneTimeProducts,
      trigger,
      metadata: defaultMetadata,
      solidMetadata,
      analyticsInfo = {},
      paymentModalTitle,
      isOnlyCard,
      isOnlyPayPal,
      errorPopupWithPayPal,
      onSuccess,
      onClose,
      onSkipClick,
      onOneClickFail,
      onError,
    },
    ref
  ) => {
    const ocRef = useRef<OneClickPaymentRef>(null);
    const ppRef = useRef<PaymentProcessorRef>(null);

    const { emailReportlLinkMode } = useRemoteConfig();

    const metadata = useMemo(() => {
      return { ...defaultMetadata, linkMode: emailReportlLinkMode };
    }, [defaultMetadata, emailReportlLinkMode]);

    const paymentSystem = useSelector(selectPaymentSystem);

    // Stripe has no order-creation step, so orderPending never cycles. Don't
    // gate the page behind the preloader for Stripe — otherwise it hangs forever.
    const [isPending, setIsPending] = useState(
      paymentSystem !== PaymentSystem.STRIPE
    );
    const hasBillingPendingBeenTrueRef = useRef(false);
    const billingPending = useSelector(
      (state: any) => state.billing.pending || state.billing.orderPending
    );

    useEffect(() => {
      if (paymentSystem === PaymentSystem.STRIPE) {
        if (isPending) {
          setIsPending(false);
        }
        return;
      }
      if (billingPending) {
        hasBillingPendingBeenTrueRef.current = true;
      }
      if (
        hasBillingPendingBeenTrueRef.current &&
        !billingPending &&
        isPending
      ) {
        setIsPending(false);
      }
    }, [billingPending, isPending, paymentSystem]);

    const handleShowPaymentPopup = useCallback(() => {
      Analytics.trackEvent(generalCategory, EVENT_ACTION.OPEN, {
        type: "order",
        currency: activeProduct?.currency,
        amount: activeProduct?.amount,
      });
      return ppRef.current?.showPaymentPopup();
    }, [activeProduct?.amount, activeProduct?.currency]);

    const {
      errorCode,
      isErrorModalOpen,
      handleCloseErrorPopup,
      handleSubmitErrorPopup,
      setIsErrorModalOpen,
      setErrorCode,
    } = useError({
      category,
      activeProduct,
      onClose,
      analyticsInfo,
      handleShowPaymentPopup,
    });

    const handleError = (args: any) => {
      onError && onError(args);
      createOrders();
    };

    const handleOneClickError = (error: any) => {
      const code = error?.code?.toString()?.replaceAll(".", "");
      if (code === ERROR_CODES.INSUFFICIENT_FUNDS) {
        setErrorCode(error?.code);
      }

      onError &&
        onError({
          message: error?.message,
          code: error?.code,
          currency: activeProduct?.currency,
          amount: activeProduct?.amount,
        });

      onOneClickFail ? onOneClickFail() : setIsErrorModalOpen(true);
      createOrders();
    };

    const { orderDetails, createOrders } = useLoadOrders({
      activeProduct,
      oneTimeProducts,
      trigger,
      metadata,
      solidMetadata,
    });

    const { updateOneClickArgs } = useOneClickPayment({
      metadata,
      solidMetadata,
    });

    const {
      handlePaymentSuccess,
      handlePaymentError,
      handleOneTimeSubmit,
      handlePayPalSubmit,
      handleOneClickSuccess,
      handleOneClickFail,
    } = usePaymentHandlers({
      activeProduct,
      onSuccess,
      onError: handleError,
      onOneClickError: handleOneClickError,
      ppRef,
    });

    const { executePaymentByMode } = usePaymentMethods({
      ocRef,
      trigger,
      metadata,
      solidMetadata,
      activeProduct,
      onShowPaymentPopup: handleShowPaymentPopup,
    });

    useImperativeHandle(ref, () => {
      return {
        purchase: async (args: OneTimePaymentArgs) => {
          if (!billingPending) {
            updateOneClickArgs(args);
            executePaymentByMode(args);
          }
        },
        showPaymentPopup: () => {
          return handleShowPaymentPopup();
        },
      };
    }, [
      billingPending,
      updateOneClickArgs,
      executePaymentByMode,
      handleShowPaymentPopup,
    ]);

    const ppProduct = useMemo(
      () => activeProduct || ({} as ProductDetails),
      [activeProduct]
    );

    return (
      <>
        {isPending && <Preloader />}

        <OneClickPayment
          ref={ocRef}
          onSuccess={handleOneClickSuccess}
          onFail={handleOneClickFail}
          orderDetails={orderDetails}
        />
        <PaymentProcessor
          ref={ppRef}
          orderDetails={orderDetails}
          activeProduct={ppProduct}
          paymentModalTitle={paymentModalTitle}
          hidePayPal={isErrorModalOpen}
          isOnlyCard={isOnlyCard}
          isOnlyPayPal={isOnlyPayPal}
          errorPopupWithPayPal={errorPopupWithPayPal}
          upsaleTrigger={trigger}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={onClose}
          onSubmit={handleOneTimeSubmit}
        />
        <ErrorPopup
          product={ppProduct}
          visible={isErrorModalOpen}
          onClose={handleCloseErrorPopup}
          onSubmit={handleSubmitErrorPopup}
          statusCode={errorCode}
          onSkipClick={onSkipClick}
          withPayPal={errorPopupWithPayPal}
          onPayPalSubmit={handlePayPalSubmit}
          onPayPalSuccess={handlePaymentSuccess}
          onPayPalError={handlePaymentError}
        />
      </>
    );
  }
);
